import type { variantConfig } from '~~/components/ui/toast'

export interface ToastOptions {
  render: string
  title?: string
  type: keyof typeof variantConfig
  icon?: string
  imageSrc?: string
  autoClose?: number
  removeAble?: boolean
}

export interface Toast extends ToastOptions {
  id: string
}

export default defineNuxtPlugin((_nuxtApp) => {
  const DEFAULT_AUTO_CLOSE = 3500
  const toasts = ref<Toast[]>([])

  const defaultIcon: Record<keyof typeof variantConfig, string> = {
    light: 'material-symbols:info',
    primary: 'material-symbols:info',
    info: 'material-symbols:info',
    success: 'material-symbols:check-circle',
    warning: 'material-symbols:warning-rounded',
    error: 'material-symbols:dangerous'
  }

  const fire = (options: ToastOptions) => {
    const toast = {
      ...options,
      id: (Math.random() + 1).toString(36).substring(10),
      icon: options.icon || defaultIcon[options.type],
      autoClose: options.autoClose ?? DEFAULT_AUTO_CLOSE,
      removeAble: options.removeAble ?? true
    }

    toasts.value.push(toast)

    if (toast.autoClose && toast.removeAble) {
      setTimeout(() => {
        removeToast(toast.id)
      }, toast.autoClose)
    }

    return toast
  }

  const update = (toast: Toast, options: ToastOptions) => {
    const findIndex = toasts.value.findIndex((item) => item.id === toast.id)

    if (findIndex < 0) return

    const newPayload = {
      ...toast,
      ...options,
      removeAble: options.removeAble || true,
      icon: options.icon || defaultIcon[options.type]
    }
    toasts.value[findIndex] = newPayload

    if (newPayload.removeAble) {
      setTimeout(() => {
        removeToast(toast.id)
      }, newPayload.autoClose ?? DEFAULT_AUTO_CLOSE)
    }
  }

  const removeToast = (id: string) => {
    const findIndex = toasts.value.findIndex((item) => item.id === id)

    if (findIndex < 0) return

    toasts.value.splice(findIndex, 1)
  }

  return {
    provide: {
      toasts,
      toast: {
        fire,
        update,
        success: (render: string, options?: ToastOptions, title?: string) => {
          return fire({ ...options, render, type: 'success', title })
        },
        error: (render: string, options?: ToastOptions, title?: string) => {
          return fire({ ...options, render, type: 'error', title })
        },
        warning: (render: string, options?: ToastOptions, title?: string) => {
          return fire({ ...options, render, type: 'warning', title })
        },
        info: (render: string, options?: ToastOptions, title?: string) => {
          return fire({ ...options, render, type: 'info', title })
        },
        light: (render: string, options?: ToastOptions, title?: string) => {
          return fire({ ...options, render, type: 'light', title })
        },
        loading: (message: string, title?: string) => {
          return fire({
            render: message,
            type: 'info',
            title,
            icon: 'svg-spinners:ring-resize',
            removeAble: false,
            autoClose: undefined
          })
        }
      }
    }
  }
})
